// HelpSupportModal: Netflix-style help and support interface
// Comprehensive support resources with FAQ, contact forms, and documentation

import React, { useState } from 'react'
import { Modal } from '../ui/Modal'
import {
  HelpCircle,
  MessageCircle,
  BookOpen,
  Send,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Mail,
  FileText,
  Video,
  Search,
  CheckCircle
} from 'lucide-react'

interface HelpSupportModalProps {
  isOpen: boolean
  onClose: () => void
}

interface ContactForm {
  subject: string
  category: string
  message: string
  email: string
}

const faqs = [
  {
    id: 1,
    question: "How do I upload images to StockTagger.ai?",
    answer: "Simply drag and drop your images onto the upload area or click to browse and select files. We support JPG, PNG, and WebP formats up to 10MB each.",
    category: "Getting Started"
  },
  {
    id: 2,
    question: "What does AI metadata generation include?",
    answer: "Our AI analyzes your images and generates titles, descriptions, keywords, and categories optimized for stock photography platforms like Shutterstock, Adobe Stock, and Getty Images.",
    category: "Features"
  },
  {
    id: 3,
    question: "How many credits do I get with each plan?",
    answer: "Starter: 500 credits/month, Professional: 2000 credits/month, Business: 10,000 credits/month, Enterprise: 50,000+ credits/month. Each image processed costs 1 credit.",
    category: "Billing"
  },
  {
    id: 4,
    question: "Can I export metadata to multiple platforms?",
    answer: "Yes! You can export your metadata in formats optimized for Shutterstock, Adobe Stock, iStock, Getty Images, or generic CSV format.",
    category: "Features"
  },
  {
    id: 5,
    question: "Is my data secure and private?",
    answer: "Absolutely. We use enterprise-grade encryption, secure cloud storage, and never share your images or metadata with third parties. You maintain full ownership of your content.",
    category: "Security"
  },
  {
    id: 6,
    question: "How do I cancel my subscription?",
    answer: "You can cancel anytime through your account settings > Manage Subscription > Billing Portal. Your subscription will remain active until the end of your billing period.",
    category: "Billing"
  }
]

const helpResources = [
  {
    id: 1,
    title: "Getting Started Guide",
    description: "Complete walkthrough for new users",
    type: "guide",
    icon: BookOpen,
    url: "#"
  },
  {
    id: 2,
    title: "Video Tutorials",
    description: "Step-by-step video instructions",
    type: "video",
    icon: Video,
    url: "#"
  },
  {
    id: 3,
    title: "API Documentation",
    description: "Technical documentation for developers",
    type: "docs",
    icon: FileText,
    url: "#"
  },
  {
    id: 4,
    title: "Best Practices",
    description: "Tips for optimizing your workflow",
    type: "guide",
    icon: BookOpen,
    url: "#"
  }
]

export function HelpSupportModal({
  isOpen,
  onClose
}: HelpSupportModalProps) {
  const [activeTab, setActiveTab] = useState<'faq' | 'contact' | 'resources'>('faq')
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [contactForm, setContactForm] = useState<ContactForm>({
    subject: '',
    category: 'general',
    message: '',
    email: ''
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const tabs = [
    { id: 'faq' as const, name: 'FAQ', icon: HelpCircle },
    { id: 'contact' as const, name: 'Contact Support', icon: MessageCircle },
    { id: 'resources' as const, name: 'Resources', icon: BookOpen }
  ]

  const contactCategories = [
    { value: 'general', label: 'General Question' },
    { value: 'billing', label: 'Billing & Subscription' },
    { value: 'technical', label: 'Technical Issue' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'bug', label: 'Bug Report' }
  ]

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000))
      setSubmitSuccess(true)
      setContactForm({
        subject: '',
        category: 'general',
        message: '',
        email: ''
      })
    } catch (error) {
      console.error('Failed to submit support request:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Help & Support"
      subtitle="Get help and contact support"
      maxWidth="2xl"
    >
      <div className="flex flex-col h-full">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex px-6">
            {tabs.map(tab => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-4 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.name}</span>
                </button>
              )
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="flex-1 p-6">
          {activeTab === 'faq' && (
            <div className="space-y-6">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search frequently asked questions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* FAQ List */}
              <div className="space-y-3">
                {filteredFaqs.map(faq => (
                  <div key={faq.id} className="border border-gray-200 rounded-lg">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === faq.id ? null : faq.id)}
                      className="w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    >
                      <span className="font-medium text-gray-900">{faq.question}</span>
                      {expandedFaq === faq.id ? (
                        <ChevronDown className="w-4 h-4 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      )}
                    </button>

                    {expandedFaq === faq.id && (
                      <div className="px-4 pb-3 border-t border-gray-100">
                        <div className="pt-3">
                          <div className="flex items-center mb-2">
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                              {faq.category}
                            </span>
                          </div>
                          <p className="text-gray-700 text-sm leading-relaxed">{faq.answer}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {filteredFaqs.length === 0 && (
                <div className="text-center py-8">
                  <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600">No FAQs found matching your search.</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'contact' && (
            <div className="space-y-6">
              {submitSuccess ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Message Sent!</h3>
                  <p className="text-gray-600 mb-4">
                    Thanks for reaching out! We'll get back to you within 24 hours.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitSuccess(false)
                      setActiveTab('faq')
                    }}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Browse FAQ while you wait
                  </button>
                </div>
              ) : (
                <>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Contact Support</h3>
                    <p className="text-gray-600 text-sm">
                      Can't find what you're looking for? Send us a message and we'll help you out.
                    </p>
                  </div>

                  <form onSubmit={handleContactSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-1">
                          Email Address
                        </label>
                        <input
                          id="contact_email"
                          type="email"
                          value={contactForm.email}
                          onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="contact_category" className="block text-sm font-medium text-gray-700 mb-1">
                          Category
                        </label>
                        <select
                          id="contact_category"
                          value={contactForm.category}
                          onChange={(e) => setContactForm(prev => ({ ...prev, category: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          {contactCategories.map(category => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="contact_subject" className="block text-sm font-medium text-gray-700 mb-1">
                        Subject
                      </label>
                      <input
                        id="contact_subject"
                        type="text"
                        value={contactForm.subject}
                        onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Brief description of your issue or question"
                        required
                      />
                    </div>

                    <div>
                      <label htmlFor="contact_message" className="block text-sm font-medium text-gray-700 mb-1">
                        Message
                      </label>
                      <textarea
                        id="contact_message"
                        rows={5}
                        value={contactForm.message}
                        onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Please provide as much detail as possible..."
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                    >
                      <Send className="w-4 h-4" />
                      <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                    </button>
                  </form>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <Mail className="w-5 h-5 text-blue-600 mt-0.5 mr-3" />
                      <div>
                        <h4 className="font-medium text-blue-800">Response Time</h4>
                        <p className="text-sm text-blue-700 mt-1">
                          We typically respond within 24 hours during business days.
                          For urgent billing issues, we'll respond within 4 hours.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'resources' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Help Resources</h3>
                <p className="text-gray-600 text-sm">
                  Browse our comprehensive documentation and tutorials.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {helpResources.map(resource => {
                  const Icon = resource.icon
                  return (
                    <a
                      key={resource.id}
                      href={resource.url}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors group"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Icon className="w-6 h-6 text-blue-600" />
                        <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                      </div>
                      <h4 className="font-medium text-gray-900 mb-1">{resource.title}</h4>
                      <p className="text-sm text-gray-600">{resource.description}</p>
                    </a>
                  )
                })}
              </div>

              <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Need More Help?</h4>
                <p className="text-sm text-gray-600 mb-4">
                  Can't find what you're looking for? Our support team is here to help.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={() => setActiveTab('contact')}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    Contact Support
                  </button>
                  <button
                    onClick={() => setActiveTab('faq')}
                    className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                  >
                    Browse FAQ
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default HelpSupportModal